import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";
import { getDisbursementAmount, formatAmount } from "../../../utils/utils";

const JlgProcessingFeeSummaryReport = () => {
  const { adminUser } = useAdminAuth();
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null); // State for branch name filter
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState(null); // State for employee name filter
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 20;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginationData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  useEffect(() => {
    const getReportData = async (id) => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/getJlgProcessingFeeSummaryReportData`,
          {
            params: { id },
          }
        );
        // console.log(
        //   "getJlgProcessingFeeSummaryReportData: " + JSON.stringify(response.data)
        // );
        if (response.data) {
          setReportData(response.data);
        } else {
          console.log("Error in fetching Data");
        }
      } catch (error) {
        console.log("Error fetching Data:", error);
      }
    };

    getReportData(adminUser.user.id);
  }, [adminUser.user.id]);

  // Extract unique branch names and employee names for dropdown options
  const branchOptions = [...new Set(reportData.map((data) => data?.branchName))]
    .filter((name) => name)
    .map((name) => ({ value: name, label: name }));

  const employeeOptions = [...new Set(reportData.map((data) => data?.username))]
    .filter((name) => name)
    .map((name) => ({ value: name, label: name }));

  // Automatically filter data when fromDate, toDate, or searchTerm changes
  useEffect(() => {
    let filtered = reportData;

    if (fromDate || toDate) {
      filtered = filtered.filter((item) => {
        // Standardize date filter to include the full end day
        if (!item.branchManagerStatusUpdatedAt) return false;

        const updatedAt = new Date(item.branchManagerStatusUpdatedAt);
        const start = fromDate ? new Date(fromDate) : new Date(0);
        start.setHours(0, 0, 0, 0);
        const end = toDate ? new Date(toDate) : new Date();
        end.setHours(23, 59, 59, 999);

        return updatedAt >= start && updatedAt <= end;
      });
    }

    filtered = filtered
      .filter((item) => {
        // Branch filter
        if (selectedBranchFilter?.value) {
          return item.branchName === selectedBranchFilter.value;
        }
        return true;
      })
      .filter((item) => {
        // Employee filter
        if (selectedEmployeeFilter?.value) {
          return item.username === selectedEmployeeFilter.value;
        }
        return true;
      });

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const branchName = String(item?.branchName || "").toLowerCase();
        const regionName = String(item?.regionName || "").toLowerCase();
        const memberName = String(item?.memberName || "").toLowerCase();
        const customerId = String(item?.customerId || "").toLowerCase();
        const loanId = String(item?.loanId || "").toLowerCase();
        const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
        const aadharNo = String(item?.aadharNo || "").toLowerCase();
        const applicationId = String(item?.ApplicationId || "").toLowerCase();

        return (
          branchName.includes(term) ||
          regionName.includes(term) ||
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    // Group by branch after filtering
    const groupedByBranch = filtered.reduce((acc, item) => {
      const key = item.branchName;
      if (!acc[key]) {
        acc[key] = {
          branchName: item.branchName,
          regionName: item.regionName,
          divisionName: item.divisionName,
          members: [],
        };
      }
      if (item.sanctionedLoanAmountBySanctionCommittee) {
        acc[key].members.push(item);
      }
      return acc;
    }, {});

    // Rest of the summarization logic remains the same
    const summarizedData = Object.values(groupedByBranch).map((branch) => {
      const numberOfAccounts = branch.members.length;
      const totalDisbursedAmount = branch.members.reduce((sum, member) => {
        return sum + getDisbursementAmount(member);
      }, 0);

      const totalRateOfInterest = branch.members.reduce((sum, member) => {
        return (
          sum + (Number(member.proposedLoanDetails?.rateOfInterest) || 0)
        );
      }, 0);

      const totalProcessingFee = branch.members.reduce((sum, member) => {
        return sum + (Number(member.processingCharge) || 0);
      }, 0);

      const totalGST = branch.members.reduce((sum, member) => {
        return sum + (Number(member.gst) || 0);
      }, 0);

      return {
        ...branch,
        numberOfAccounts,
        totalDisbursedAmount,
        averageLoanSize: numberOfAccounts
          ? totalDisbursedAmount / numberOfAccounts
          : 0,
        averageRateOfInterest: numberOfAccounts
          ? totalRateOfInterest / numberOfAccounts
          : 0,
        totalProcessingFee,
        totalSGST: totalGST / 2,
        totalCGST: totalGST / 2,
      };
    });

    setFilteredData(summarizedData);
    setCurrentPage(1);
  }, [
    fromDate,
    toDate,
    reportData,
    selectedBranchFilter,
    selectedEmployeeFilter,
    searchTerm,
  ]);

  // Define columns with label and render logic
  const columns = [
    { label: "S No", render: (data, index) => index + 1 },
    { label: "State", render: (data) => "Tamilnadu" },
    { label: "Region", render: (data) => data.regionName },
    { label: "Division", render: (data) => data.divisionName },
    { label: "Branch Name", render: (data) => data.branchName },
    { label: "No. of Accounts", render: (data) => data.numberOfAccounts },
    {
      label: "Disbursement Amount",
      render: (data) => formatAmount(data.totalDisbursedAmount),
    },
    {
      label: "Average Loan Size",
      render: (data) => formatAmount(data.averageLoanSize),
    },
    {
      label: "Interest Rate",
      render: (data) => formatAmount(data.averageRateOfInterest),
    },
    {
      label: "Processing Fee",
      render: (data) => formatAmount(data.totalProcessingFee),
    },
    {
      label: "SGST",
      render: (data) => formatAmount(data.totalSGST),
    },
    {
      label: "CGST",
      render: (data) => formatAmount(data.totalCGST),
    },
  ];

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">JLG Processing Fee Summary Report</h1>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`w-100 ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row className="w-100" style={{ fontWeight: "500" }}>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <Form.Control
                      type="date"
                      placeholder="Enter Date"
                      value={fromDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setFromDate(val);
                        if (val && toDate && selectedBranchFilter && selectedEmployeeFilter) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <Form.Control
                      type="date"
                      placeholder="Enter Date"
                      value={toDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setToDate(val);
                        if (fromDate && val && selectedBranchFilter && selectedEmployeeFilter) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={3} md={6} className="mb-2">
                  <Form.Label>Branch</Form.Label>
                  <Select
                    value={selectedBranchFilter}
                    onChange={(val) => {
                      setSelectedBranchFilter(val);
                      if (fromDate && toDate && val && selectedEmployeeFilter) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={branchOptions}
                    isClearable
                    placeholder="Filter by Branch Name"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={3} md={6} className="mb-2">
                  <Form.Label>Employee</Form.Label>
                  <Select
                    value={selectedEmployeeFilter}
                    onChange={(val) => {
                      setSelectedEmployeeFilter(val);
                      if (fromDate && toDate && selectedBranchFilter && val) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={employeeOptions}
                    isClearable
                    placeholder="Filter by Employee Name"
                    className="filter-dropdown"
                  />
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={paginationData}
                reportName="JlgProcessingFeeSummaryReport"
                excelData={filteredData}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={filteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JlgProcessingFeeSummaryReport;
